/* eslint-disable import/no-unused-modules */
import type { GatsbyBrowser } from 'gatsby';
import React from 'react';
import ReactModal from 'react-modal';

import { getAmplitudeKey } from 'src/components/common/amplitude';
import type { PageContext } from 'src/components/common/page-context';
import type { TemplatePageContext } from 'src/components/common/template-page-context';
import PageLayout from 'src/components/layout/page-layout';
import { getPrimaryBuildTypeForBuildDomain } from 'src/enums/build/build-domain';
import {
  getAllLanguagesForBuildType,
  getLanguageUrlPrefixForBuildType,
} from 'src/enums/build/build-type';
import { isOnAdminPath } from 'src/helpers/admin-path';
import { fallbackLanguage, loadLookupAsync } from 'src/translators/static-text-translator';

import { buildDomain } from './build-scripts/environment';
import { userHasConsentedToAnalyticsCookieCategory } from './src/cookies/cookie-types/analytical-cookies';

const preloadLanguage = (pathname: string): Promise<unknown> => {
  // Best attempt to get the correct language bundle loaded as soon as possible.
  const firstSegment = pathname.split('/').find(Boolean) ?? '';
  const buildType = getPrimaryBuildTypeForBuildDomain(buildDomain);
  const likelyLanguageTarget =
    getAllLanguagesForBuildType(buildType).find(
      (lang) => getLanguageUrlPrefixForBuildType(lang, buildType) === firstSegment
    ) ?? fallbackLanguage;
  return loadLookupAsync(likelyLanguageTarget);
};

let amplitudeInitialized = false;
let amplitudeExperimentLoaded = false;
let amplitudeConsentAllowed = false;

const loadAmplitudeExperiment = (amplitudeKey: string): void => {
  if (amplitudeExperimentLoaded || document.getElementById('amplitude-experiment-script')) {
    return;
  }

  const script = document.createElement('script');
  script.id = 'amplitude-experiment-script';
  script.async = true;
  script.src = `https://cdn.eu.amplitude.com/script/${amplitudeKey}.experiment.js`;
  document.head.appendChild(script);

  amplitudeExperimentLoaded = true;
};

const loadAmplitude = () => {
  if (amplitudeInitialized || isOnAdminPath() || !amplitudeConsentAllowed) {
    return;
  }

  Promise.all([
    import('@amplitude/analytics-browser'),
    import('@amplitude/plugin-session-replay-browser'),
    import('@amplitude/engagement-browser'),
  ])
    .then(([{ init, add, setOptOut }, { sessionReplayPlugin }, { plugin: engagementPlugin }]) => {
      const amplitudeKey = getAmplitudeKey(buildDomain);

      if (!amplitudeKey || !amplitudeConsentAllowed) {
        return;
      }

      window.launchAmplitude = function ({ allow }: { allow: boolean }) {
        amplitudeConsentAllowed = allow;
        setOptOut(!allow);

        if (allow) {
          loadAmplitudeExperiment(amplitudeKey);
        }
      };

      const sessionReplayTracking = sessionReplayPlugin({
        sampleRate: 0.03,
      });

      add(sessionReplayTracking);
      add(engagementPlugin());

      init(amplitudeKey, {
        serverZone: 'EU',
        optOut: false,
        cookieOptions: {
          secure: true,
        },
      });

      loadAmplitudeExperiment(amplitudeKey);

      amplitudeInitialized = true;
    })
    // eslint-disable-next-line no-console
    .catch(console.error);
};

// eslint-disable-next-line @typescript-eslint/no-misused-promises
export const onClientEntry: GatsbyBrowser['onClientEntry'] = async () => {
  // Tell react-modal which element to hide from screen readers while a modal is open (a11y).
  // Applies globally to all ReactModal instances.
  ReactModal.setAppElement('#___gatsby');
  amplitudeConsentAllowed = userHasConsentedToAnalyticsCookieCategory();

  window.launchAmplitude = ({ allow }: { allow: boolean }) => {
    amplitudeConsentAllowed = allow;

    if (allow) {
      loadAmplitude();
    }
  };

  if (typeof window.fetch === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- TODO assess
    // @ts-ignore-next-line
    void import('whatwg-fetch');
  }

  // Object-fit/Object-position polyfill for gatsby-image (IE)
  const testImg = document.createElement(`img`);
  if (
    typeof testImg.style.objectFit === 'undefined' ||
    typeof testImg.style.objectPosition === 'undefined'
  ) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- TODO assess
    // @ts-ignore-next-line
    import('object-fit-images')
      .then(({ default: ObjectFitImages }) => (ObjectFitImages as () => void)())
      // eslint-disable-next-line no-console
      .catch(console.error);
  }

  if (!amplitudeInitialized && typeof window !== 'undefined' && !isOnAdminPath()) {
    window.addEventListener('load', loadAmplitude, { once: true });
  }

  await preloadLanguage(window.location.pathname);
};

export const onPrefetchPathname: GatsbyBrowser['onPrefetchPathname'] = ({ pathname }) => {
  void preloadLanguage(pathname);
};

export const wrapPageElement: GatsbyBrowser<
  unknown,
  PageContext | TemplatePageContext
>['wrapPageElement'] = ({ element, props }) => <PageLayout props={props}>{element}</PageLayout>;
